// frontend/src/pages/maintenance/ContractTemplatesList.tsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  Sheet,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Switch,
} from '@mui/joy';
import { Plus, PencilSimple as Edit, Trash as Trash2, FileText } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlanImpact } from './ContractWorkflows';
import { contractTemplateApi } from '../../lib/maintenanceApi';
import type {
  MaintenanceContractTemplate,
  MaintenanceContractTemplateForm,
  MaintenanceFrequency,
} from '../../types/maintenance';
import { FREQUENCY_LABELS } from '../../types/maintenance';

const ContractTemplatesList: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<MaintenanceContractTemplate | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch templates
  const { data: templatesResponse, isLoading } = useQuery({
    queryKey: ['maintenance-templates'],
    queryFn: () => contractTemplateApi.list(),
  });

  const templates = templatesResponse?.results || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => contractTemplateApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-templates'] });
    },
  });

  const handleDelete = async (template: MaintenanceContractTemplate) => {
    if (window.confirm(`Delete template "${template.name}"?`)) {
      await deleteMutation.mutateAsync(template.id);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography level="h2">Contract Templates</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Manage maintenance contract templates
          </Typography>
        </Box>
        <Button startDecorator={<Plus size={18} />} onClick={() => setIsCreateModalOpen(true)}>
          New Template
        </Button>
      </Box>

      {/* Templates Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <FileText size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <Typography level="h4" sx={{ mb: 1 }}>
              No templates found
            </Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
              Create templates to quickly set up maintenance contracts
            </Typography>
            <Button startDecorator={<Plus size={18} />} onClick={() => setIsCreateModalOpen(true)}>
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
          <Table hoverRow stickyHeader>
            <thead>
              <tr>
                <th>Name</th>
                <th>Frequency</th>
                <th>Fee</th>
                <th>Duration</th>
                <th>Status</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {template.name}
                    </Typography>
                    {template.description && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {template.description}
                      </Typography>
                    )}
                  </td>
                  <td>{FREQUENCY_LABELS[template.frequency]}</td>
                  <td>${template.fee_per_appointment}</td>
                  <td>{template.duration_months} months</td>
                  <td>
                    <Chip
                      size="sm"
                      color={template.is_active ? 'success' : 'neutral'}
                      variant="soft"
                    >
                      {template.is_active ? 'Active' : 'Inactive'}
                    </Chip>
                  </td>
                  <td>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => setEditingTemplate(template)}
                      >
                        <Edit size={16} />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(template)}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Create/Edit Modal */}
      <TemplateModal
        open={isCreateModalOpen || !!editingTemplate}
        template={editingTemplate}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingTemplate(null);
        }}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['maintenance-templates'] });
          setIsCreateModalOpen(false);
          setEditingTemplate(null);
        }}
      />
    </Box>
  );
};

// Template Modal
interface TemplateModalProps {
  open: boolean;
  template: MaintenanceContractTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({ open, template, onClose, onSaved }) => {
  const [formData, setFormData] = useState<MaintenanceContractTemplateForm>({
    name: '',
    description: '',
    frequency: 'annual',
    fee_per_appointment: '0.00',
    duration_months: 12,
    is_active: true,
    auto_renew: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description,
        frequency: template.frequency,
        fee_per_appointment: template.fee_per_appointment,
        duration_months: template.duration_months,
        is_active: template.is_active,
        auto_renew: template.auto_renew,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        frequency: 'annual',
        fee_per_appointment: '0.00',
        duration_months: 12,
        is_active: true,
        auto_renew: true,
      });
    }
  }, [template]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (template) {
        await contractTemplateApi.update(template.id, formData);
      } else {
        await contractTemplateApi.create(formData);
      }
      onSaved();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 'min(95vw, 600px)', maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <Typography level="h4">{template ? 'Edit Template' : 'Create Template'}</Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          {template && <PlanImpact id={template.id} fee={formData.fee_per_appointment} frequency={formData.frequency} />}
          <FormControl required>
            <FormLabel>Template Name</FormLabel>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Basic Annual Maintenance"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              minRows={2}
            />
          </FormControl>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ flex: 1, minWidth: 0 }} required>
              <FormLabel>Frequency</FormLabel>
              <Select
                value={formData.frequency}
                onChange={(_, value) =>
                  setFormData({ ...formData, frequency: value as MaintenanceFrequency })
                }
              >
                {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: 0 }} required>
              <FormLabel>Fee per Appointment</FormLabel>
              <Input
                type="number"
                startDecorator="$"
                value={formData.fee_per_appointment}
                onChange={(e) =>
                  setFormData({ ...formData, fee_per_appointment: e.target.value })
                }
              />
            </FormControl>
          </Stack>

          <FormControl>
            <FormLabel>Duration (months)</FormLabel>
            <Input
              type="number"
              value={formData.duration_months}
              onChange={(e) =>
                setFormData({ ...formData, duration_months: Number(e.target.value) })
              }
            />
          </FormControl>

          <Stack direction="row" spacing={3}>
            <FormControl orientation="horizontal">
              <FormLabel>Active</FormLabel>
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </FormControl>
            <FormControl orientation="horizontal">
              <FormLabel>Auto Renew</FormLabel>
              <Switch
                checked={formData.auto_renew}
                onChange={(e) => setFormData({ ...formData, auto_renew: e.target.checked })}
              />
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting} disabled={!formData.name}>
              {template ? 'Save Changes' : 'Create Template'}
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default ContractTemplatesList;
